import { useCallback, useEffect, useRef } from "react";
import { io } from "socket.io-client";

/**
 * useSocket
 * Connects to the backend socket.io server, joins the current user's room,
 * and calls onNewMessage / onCrmEvent when socket events arrive.
 *
 * @param {string} userId         — current user's _id
 * @param {function} onNewMessage — ({ requirementId, requirementName, message }) => void
 * @param {function} onCrmEvent   — (event, payload) => void
 */
export default function useSocket(userId, onNewMessage, onCrmEvent) {
  const socketRef = useRef(null);
  const isWatchingCrmBoardRef = useRef(false);
  const watchedRequirementIdRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    const apiUrl = process.env.REACT_APP_API_URL || "";
    let URL = "https://rmt.cloudstakes.com";
    let socketPath = "/api/socket.io";

    if (apiUrl) {
      try {
        URL = new URL(apiUrl).origin;
      } catch (e) {
        console.error("Invalid REACT_APP_API_URL:", apiUrl);
      }
    } else if (window.location.hostname === "localhost") {
      URL = "http://localhost:9003";
    }

    if (URL.includes("localhost") || URL.includes("127.0.0.1") || URL.includes("9003")) {
      socketPath = undefined;
    }

    console.log("Connecting socket to:", URL, "with path:", socketPath || "default");
    const socketOptions = {
      transports: ["websocket"],
    };
    if (socketPath) {
      socketOptions.path = socketPath;
    }
    const socket = io(URL, socketOptions);

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket Connected:", socket.id);
      socket.emit("join", userId);

      // Re-subscribe to rooms on reconnect
      if (isWatchingCrmBoardRef.current) {
        console.log("Re-joining CRM board room...");
        socket.emit("join_crm_board");
      }
      if (watchedRequirementIdRef.current) {
        console.log("Re-watching requirement:", watchedRequirementIdRef.current);
        socket.emit("watch_requirement", watchedRequirementIdRef.current);
      }
    });

    socket.on("connect_error", (err) => {
      console.error("Socket Error:", err);
    });

    // Listen for new messages directed at this user
    socket.on("new_message", (payload) => {
      onNewMessage(payload);
    });

    // Listen for CRM Board events
    if (onCrmEvent) {
      socket.on("card:created", (payload) => {
        onCrmEvent("card:created", payload);
      });
      socket.on("card:updated", (payload) => {
        onCrmEvent("card:updated", payload);
      });
      socket.on("card:moved", (payload) => {
        onCrmEvent("card:moved", payload);
      });
      socket.on("card:deleted", (payload) => {
        onCrmEvent("card:deleted", payload);
      });
    }

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId, onNewMessage, onCrmEvent]);

  const watchRequirement = useCallback((requirementId) => {
    watchedRequirementIdRef.current = requirementId;
    if (socketRef.current && requirementId) {
      socketRef.current.emit("watch_requirement", requirementId);
    }
  }, []);

  const unwatchRequirement = useCallback((requirementId) => {
    watchedRequirementIdRef.current = null;
    if (socketRef.current && requirementId) {
      socketRef.current.emit("unwatch_requirement", requirementId);
    }
  }, []);

  const joinCrmBoard = useCallback(() => {
    isWatchingCrmBoardRef.current = true;
    if (socketRef.current) {
      socketRef.current.emit("join_crm_board");
    }
  }, []);

  const leaveCrmBoard = useCallback(() => {
    isWatchingCrmBoardRef.current = false;
    if (socketRef.current) {
      socketRef.current.emit("leave_crm_board");
    }
  }, []);

  return { watchRequirement, unwatchRequirement, joinCrmBoard, leaveCrmBoard };
}
